import { compose, map, path, pathOr } from 'ramda'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { bindActionCreators } from 'redux'

import { MIC_ACCESS_ERROR } from '@/app/definitions'
import { selectToken } from '@/app/module/auth/store/selectors'
import { getCustomFields } from '@/app/module/custom-fields/store/actions'
import { getGroups, navigateGroups } from '@/app/module/groups/store/actions'
import {
  selectPage as selectAllGroupTypes,
  selectOrdered as selectAllGroupTypesOrdered,
  selectItemConfig as selectGroupTypesItemConfig,
} from '@/app/module/groups/store/selectors'
import { getSenderIdList } from '@/app/module/logs/store/actions'
import { selectPersonalizationList } from '@/app/module/logs/store/selectors'
import { createNotification } from '@/app/module/notifications/store/actions'
import { getSegments, navigateSegments } from '@/app/module/segments/store/actions'
import {
  selectPage as selectAllSegmentTypes,
  selectOrdered as selectAllSegmentTypesOrdered,
  selectItemConfig as selectSegmentTypesItemConfig,
} from '@/app/module/segments/store/selectors'
import { selectOrgId, selectUserTimezone } from '@/app/module/user/store/selectors'
import {
  selectCountriesTimezones,
  selectDefaultCountryCode,
  selectLanguages,
  selectSpeechLanguages,
  selectVoices,
} from '@/app/module/utils/store/selectors'
import config from '@/config'

import Campaigns from './component'
import Manage from './component/item/manage'
import ItemContainer from './component/item.container'
import {
  selectCampaignItemConfig,
  selectGroupsHeaders,
  selectHeaders,
  selectMessagesState,
  selectSegmentsHeaders,
} from './store/selectors'
import { selectDidChange, setDidChange } from './store/slices/campaign-item.slice'

const pageSizeValues = compose(
  map((val) => ({ label: val, value: val })),
  path(['modules', 'campaigns', 'pageSize', 'values']),
)(config)

const selector = ({ customFields, user, utils }) => ({
  error: user.details.error,
  loading: user.details.loading || user.organization.loading || customFields.loading,
  timezone: selectUserTimezone({ user, utils }),
  timezones: pathOr({}, ['countriesTimezonesList', 'data'], utils),
  countriesTimezones: selectCountriesTimezones({ utils }),
  token: selectToken(),
  orgId: selectOrgId({ user }),
  pageSizeValues,
  headers: selectHeaders(),
  specs: selectCampaignItemConfig({ user, utils }),
  personalizationList: selectPersonalizationList({ customFields }),
})

const dispatcher = (dispatch) =>
  bindActionCreators(
    {
      createNotification,
      getCustomFields,
    },
    dispatch,
  )

export default connect(selector, dispatcher)(withRouter(Campaigns))

const itemSelector = ({ user, campaignItem, customFields, logs, groups, segments, utils }) => ({
  campaignHydrateProps: ['token', 'orgId'],
  error: {
    ...user.details.error,
  },
  token: selectToken(),
  orgId: selectOrgId({ user }),
  changed: selectDidChange({ campaignItem }),
  timezone: selectUserTimezone({ user, utils }),
  timezones: pathOr({}, ['countriesTimezonesList', 'data'], utils),
  countriesTimezones: selectCountriesTimezones({ utils }),
  countryCode: selectDefaultCountryCode({ utils }),
  messages: selectMessagesState({ customFields, logs }),
  groups: {
    loading: pathOr(false, ['list', 'loading'], groups),
    error: pathOr({}, ['list', 'error'], groups),
    data: selectAllGroupTypes({ groups, user }),
    headers: selectGroupsHeaders(),
    ordered: selectAllGroupTypesOrdered({ groups }),
    specs: selectGroupTypesItemConfig({ user }),
    total: pathOr(0, ['list', 'data', 'total'], groups),
    query: {
      page: pathOr(1, ['list', 'data', 'page'], groups),
      size: pathOr(0, ['list', 'data', 'size'], groups),
      searchTerm: pathOr('', ['list', 'data', 'search'], groups),
    },
  },
  segments: {
    loading: pathOr(false, ['list', 'loading'], segments),
    error: pathOr({}, ['list', 'error'], segments),
    data: selectAllSegmentTypes({ segments, user }),
    headers: selectSegmentsHeaders(),
    ordered: selectAllSegmentTypesOrdered({ segments }),
    specs: selectSegmentTypesItemConfig({ user }),
    total: pathOr(0, ['list', 'data', 'total'], segments),
    query: {
      page: pathOr(1, ['list', 'data', 'page'], segments),
      size: pathOr(0, ['list', 'data', 'size'], segments),
      searchTerm: pathOr('', ['list', 'data', 'search'], segments),
    },
  },
  languageList: selectLanguages({ utils }),
  voiceList: selectVoices({ utils }),
  speechLanguageList: selectSpeechLanguages({ utils }),
})

const itemDispatcher = (dispatch) =>
  bindActionCreators(
    {
      campaignHydrate:
        ({ token, orgId }) =>
        () => {
          dispatch(navigateGroups({}))
          dispatch(navigateSegments({}))
          return Promise.all([
            dispatch(getCustomFields({ token, orgId })),
            dispatch(getSenderIdList({ token, orgId })),
            dispatch(
              getGroups({
                token,
                orgId,
                query: {
                  page: 1,
                  size: path(['modules', 'groups', 'pageSize', 'default'], config),
                },
              }),
            ),
            dispatch(
              getSegments({
                token,
                orgId,
                query: {
                  page: 1,
                  size: path(['modules', 'segments', 'pageSize', 'default'], config),
                },
              }),
            ),
          ])
        },
      createNotification,
      onMicAccessError: () =>
        dispatch(
          createNotification({
            'mic-access': {
              type: 'error',
              message: MIC_ACCESS_ERROR,
            },
          }),
        ),
      setChanged: setDidChange,
    },
    dispatch,
  )

export const CampaignsItem = connect(itemSelector, itemDispatcher)(ItemContainer)

const manageSelector = ({ user, utils }) => ({
  orgId: selectOrgId({ user }),
  timezone: selectUserTimezone({ user, utils }),
  timezones: pathOr({}, ['countriesTimezonesList', 'data'], utils),
  countriesTimezones: selectCountriesTimezones({ utils }),
  countryCode: selectDefaultCountryCode({ utils }),
})

export const CampaignManager = connect(manageSelector)(Manage)
